// @flow
import * as React from "react";
import {reisy, use} from "reisy";

import type {Publishable} from "../api/types.js";
import Image from "../components/image.js";
import Link from "../components/link.js";
import TimeAgo from '../components/time.js';
import Heading from '../components/heading';
import TagList from '../components/taglist.js';


export const styles = reisy`
@namespace Slide;

ratio: 2;
mobileRatio: 2;
showSubtitle: true;
showDesc: true;
showTags: true;

Wrapper {
	position: relative;
	width: 100%;
	margin: 2em 0;


	/* Can't use padding % inside parents with display flex, inconsistent browser behavior */
	$Deck.Wrapper & {
		display: flex;
	}
}
wrapped {
	padding: 0;
	margin: 0;
	height: 100%;
}

InnerWrapper {
	display: flex;
	position: relative;
	width: 100%;

	$Media.medium {
		padding-top: 30%;
	}

	$Media.large {
		padding-top: 25%;
	}
}

Slide {
	display: flex;
	align-self: flex-end;
	width: 100%;
	height: 100%;
	padding: 32px 0;
	text-decoration: none;
	box-sizing: border-box;

	$Media.smallest {
		font-size: .75em;
	}
	$Media.medium {
		position: absolute;
		top: 0;
		left: 0;
		bottom: 0;
		right: 0;
	}
}

Slide-full {
	padding: 2em;
}

Image {
}

Header {
	z-index: 1;
	line-height: 1.33;
    box-sizing: border-box;
}

HeaderInnerWrap {
	overflow: hidden;
}

Header-full {
	display: flex;
	flex-direction: column;
	justify-content: center;
	flex: 1 0;
	align-items: center;
	text-align: center;
	width: 100%;
	background: rgba(192, 192, 192, 0.4);
	color: rgba(0, 0, 0, 0.6);
	font-size: 1.5em;
	padding: 0.75em;

	& $Title {
		color: inherit;
	}

	& $Subtitle {
		color: inherit;
	}

	$Media.medium {
		font-size: 1.75em;
	}

	$Media.larger {
		font-size: 2.25em;
	}
}

Header-small {
	background: rgba(255, 255, 255, 0.5);
	color: white;
	text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
	font-size: 1em;
	padding: .5em;
	line-height: 1.33;
	align-self: flex-end;
	margin: 0 48px;
	max-height: 100%;

	$Kosk.isIE & {
		position: absolute;
		bottom: 0;
	}

	& $Title {
		color: inherit;
	}

	& $Subtitle {
		color: inherit;
	}

	$Media.small {
		font-size: 1.15em;
		max-width: 66%;
	}

	$Media.medium {
		margin: 0 64px;
	}
}

Title {
	@extends Typography.SlideTitle;
}
Subtitle {
	@extends Typography.SlideSubtitle;
	@extends Helpers.Ellipsis;
	margin-top: .25em;
	$Media.small {
		white-space: normal;
	}
}
Desc {
	@extends Typography.SlideDesc;
	@extends Helpers.Ellipsis;
	margin-top: .25em;
	$Media.small {
		white-space: normal;
	}
}
Time {
	font-family: $Fonts.aside;
	font-size: .7em;
	color: $Colors.TextSubtle;
}
Button {
	font-family: $Fonts.aside;
	font-size: 0.8em;
	line-height: 1;
	text-shadow: none;
	display: inline-block;
	margin-top: .75em;
	padding: .25em .4em;
	color: white;
	background: #528de0;
	border: 1px solid #3373cc;
	border-radius: 4px;
	box-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}
`;

type Props = {
	content: Publishable;
	parent?: Publishable;
	className?: string;
	wrapped?: boolean;
	variant?: "small" | "full";
	button?: string;
};

const settings = reisy.namespace("Settings");

const Slide = ({content, parent, className, wrapped = false, variant = "small", button}: Props) => {
	const taglist = styles.showTags && content.tags ?
		<TagList publishable={content} filter={styles.showTags.startsWith && styles.showTags.startsWith('/') ? i => i.name.match(new RegExp(styles.showTags.slice(1,-1))) : undefined} /> 
				: null;

	const to = content.options && content.options.href || content.href || {append: [parent, content]};
	const target = content.type === 'link' ? (content.options && content.options.target || '_blank') : null;

	return (
		<article {...use(styles.Wrapper, wrapped && styles.wrapped, className, content.options && content.options.className)}>
			<div {...use(styles.InnerWrapper)}>
				<Link to={to}  {...(target ? { target } : {})}>
					<Image
						style={styles.Image}
						image={content.image}
						ratio={(
								// normal ratio for first articles
								(IS_BROWSER && window.innerWidth < 900)
									? styles.mobileRatio
									: styles.ratio) || 2}
						cover absolute sizes="100vw"
						imageOptions={content.imageOptions} />
				</Link>
				<Link {...use(styles.Slide, styles[`Slide-${variant}`])} to={to} {...(target ? { target } : {})}>
					<header {...use(styles.Header, styles[`Header-${variant}`])}>
						<div {...use(styles.HeaderInnerWrap)}>
							<Heading lvl={2} {...use(styles.Title)}>{content.title}</Heading>
							{(styles.showSubtitle && content.subTitle) && <p {...use(styles.Subtitle)}>{content.subTitle}</p>}
							{(styles.showDesc && content.description) && <div {...use(styles.Desc)}>{content.description}</div>}
							{taglist}
							{settings.ShowTime && <TimeAgo {...use(styles.Time)} time={content.publishDate} />}
							{button && <div {...use(styles.Button)}>{button}</div>}
						</div>
					</header>
				</Link>
			</div>
		</article>
	);
}

export default Slide;



// WEBPACK FOOTER //
// ./lib/publishables/slide.js